import type { Coordinates, DistanceResult } from '../../../services/googleMapsDistanceService';
import { googleMapsDistanceService } from '../../../services/googleMapsDistanceService';
import { Routemize_Constants_AppointmentStatus } from '../../../api/models/Routemize_Constants_AppointmentStatus';
import type { SlotRescheduleCandidate } from '../../../business/appointments/slotRescheduleCandidateTypes';
import type { Appointment } from '../types';
import { calculateHaversineDistance } from './appointmentHelpers';
import { proximityTierFromMiles, type SlotProximityTier } from './slotProximityTiers';

const MATRIX_DEST_CHUNK = 25;
const CANCELLED_MAX_PROXIMITY_MILES = 15;
const HAVERSINE_PREFILTER = 24;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isLikelyAppointmentGuid(id: string): boolean {
  return UUID_RE.test(id);
}

export type RankedSlotCandidate = SlotRescheduleCandidate & {
  previousStopName?: string;
  nextStopName?: string;
  proximityMiles: number;
  distanceFromPrevMiles: number | null;
  distanceToNextMiles: number | null;
  driveFromPrevMinutes: number | null;
  driveToNextMinutes: number | null;
  tier: SlotProximityTier;
  isEstimate: boolean; // true when proximity uses haversine fallback
  timeTight: boolean;
};

export function resolveSlotAnchors(
  prevAppointment: Appointment,
  nextAppointment: Appointment,
  startingLocation?: { lat: number; lng: number }
): { before: Coordinates | null; after: Coordinates | null } {
  const prevHas =
    prevAppointment.latitude != null &&
    prevAppointment.longitude != null &&
    !(prevAppointment.latitude === 0 && prevAppointment.longitude === 0);
  const nextHas =
    nextAppointment.latitude != null &&
    nextAppointment.longitude != null &&
    !(nextAppointment.latitude === 0 && nextAppointment.longitude === 0);

  const before: Coordinates | null = prevHas
    ? { lat: prevAppointment.latitude!, lng: prevAppointment.longitude! }
    : startingLocation ?? null;

  const after: Coordinates | null = nextHas
    ? { lat: nextAppointment.latitude!, lng: nextAppointment.longitude! }
    : startingLocation ?? null;

  return { before, after };
}

function haversineProximityMiles(
  candidate: Coordinates,
  before: Coordinates | null,
  after: Coordinates | null
): number {
  const toBefore = before ? calculateHaversineDistance(candidate, before) : Number.POSITIVE_INFINITY;
  const toAfter = after ? calculateHaversineDistance(candidate, after) : Number.POSITIVE_INFINITY;
  return Math.min(toBefore, toAfter);
}

function mapsReady(): boolean {
  return typeof google !== 'undefined' && !!google.maps;
}

function estimateDriveMinutesFromMiles(miles: number): number {
  const AVG_SPEED_MPH = 45;
  return Math.max(Math.round((miles / AVG_SPEED_MPH) * 60), 1);
}

function scheduledStatuses(): Set<string> {
  return new Set<string>([
    Routemize_Constants_AppointmentStatus.SCHEDULED,
    'Scheduled',
  ]);
}

function cancelledStatuses(): Set<string> {
  return new Set<string>([Routemize_Constants_AppointmentStatus.CANCELLED, 'Cancelled']);
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Ranks reschedule candidates by min driving distance to slot anchors (before / after).
 * Uses Haversine to prefilter, then refines top candidates with Distance Matrix when Maps is ready.
 */
export async function rankCandidatesForSlot(params: {
  candidates: SlotRescheduleCandidate[];
  before: Coordinates | null;
  after: Coordinates | null;
  previousStopName?: string;
  nextStopName?: string;
  excludeAppointmentIds: string[];
  totalGapMinutes: number;
  slotDate?: Date;
}): Promise<{ scheduled: RankedSlotCandidate[]; cancelled: RankedSlotCandidate[] }> {
  const { candidates, before, after, previousStopName, nextStopName, excludeAppointmentIds, totalGapMinutes, slotDate } = params;
  const exclude = new Set(excludeAppointmentIds.filter(isLikelyAppointmentGuid));
  const now = new Date();

  if (!before && !after) {
    return { scheduled: [], cancelled: [] };
  }

  const schedSet = scheduledStatuses();
  const cancelSet = cancelledStatuses();

  const eligible = candidates.filter((c) => {
    if (exclude.has(c.id)) return false;
    const st = String(c.status);
    if (!schedSet.has(st) && !cancelSet.has(st)) return false;
    if (schedSet.has(st) && c.meetingStartUtc <= now) return false;
    if (schedSet.has(st) && slotDate && isSameCalendarDay(c.meetingStartUtc, slotDate)) return false;
    return true;
  });

  const withHaversine = eligible.map((c) => {
    const coord = { lat: c.latitude, lng: c.longitude };
    const hMi = haversineProximityMiles(coord, before, after);
    const workMinutes =
      (c.timeAtLocationMinutes ?? 60) + (c.preBufferMinutes ?? 0) + (c.postBufferMinutes ?? 0);
    return { c, hMi, workMinutes };
  });

  withHaversine.sort((a, b) => a.hMi - b.hMi);
  const prefilter = withHaversine.slice(0, HAVERSINE_PREFILTER);

  const origins: Coordinates[] = [];
  if (before) origins.push(before);
  if (after && (!before || before.lat !== after.lat || before.lng !== after.lng)) {
    origins.push(after);
  }

  const refineDriving = mapsReady() && origins.length > 0;

  const refined: RankedSlotCandidate[] = [];

  for (let i = 0; i < prefilter.length; i += MATRIX_DEST_CHUNK) {
    const slice = prefilter.slice(i, i + MATRIX_DEST_CHUNK);
    const dests = slice.map((s) => ({ lat: s.c.latitude, lng: s.c.longitude }));

    let matrix: (DistanceResult | null)[][] | null = null;
    if (refineDriving) {
      matrix = await googleMapsDistanceService.calculateDistanceMatrix(origins, dests);
    }

    slice.forEach((row, j) => {
      const { c, workMinutes } = row;
      const coord = { lat: c.latitude, lng: c.longitude };
      let proximityMiles: number;
      let distanceFromPrevMiles: number | null = null;
      let distanceToNextMiles: number | null = null;
      let driveFromPrevMinutes: number | null = null;
      let driveToNextMinutes: number | null = null;
      let isEstimate: boolean;

      if (matrix && matrix.length === origins.length && matrix[0]?.length === dests.length) {
        const first = matrix[0]?.[j];
        if (before && first && typeof first.distance === 'number') {
          distanceFromPrevMiles = first.distance;
          driveFromPrevMinutes = typeof first.duration === 'number' ? first.duration : null;
        }
        const hasDistinctAfter = !!after && (!before || before.lat !== after.lat || before.lng !== after.lng);
        if (hasDistinctAfter) {
          const second = matrix[1]?.[j];
          if (second && typeof second.distance === 'number') {
            distanceToNextMiles = second.distance;
            driveToNextMinutes = typeof second.duration === 'number' ? second.duration : null;
          }
        } else if (after && first && typeof first.distance === 'number') {
          distanceToNextMiles = first.distance;
          driveToNextMinutes = typeof first.duration === 'number' ? first.duration : null;
        }

        const best = Math.min(
          distanceFromPrevMiles ?? Number.POSITIVE_INFINITY,
          distanceToNextMiles ?? Number.POSITIVE_INFINITY
        );
        if (Number.isFinite(best)) {
          proximityMiles = best;
          isEstimate = false;
        } else {
          proximityMiles = row.hMi;
          if (before) {
            distanceFromPrevMiles = calculateHaversineDistance(coord, before);
            driveFromPrevMinutes = estimateDriveMinutesFromMiles(distanceFromPrevMiles);
          }
          if (after) {
            distanceToNextMiles = calculateHaversineDistance(coord, after);
            driveToNextMinutes = estimateDriveMinutesFromMiles(distanceToNextMiles);
          }
          isEstimate = true;
        }
      } else {
        proximityMiles = row.hMi;
        if (before) {
          distanceFromPrevMiles = calculateHaversineDistance(coord, before);
          driveFromPrevMinutes = estimateDriveMinutesFromMiles(distanceFromPrevMiles);
        }
        if (after) {
          distanceToNextMiles = calculateHaversineDistance(coord, after);
          driveToNextMinutes = estimateDriveMinutesFromMiles(distanceToNextMiles);
        }
        isEstimate = true;
      }

      const tier = proximityTierFromMiles(proximityMiles);
      const requiredMinutes =
        workMinutes +
        (driveFromPrevMinutes ?? 0) +
        (driveToNextMinutes ?? 0);
      const timeTight = requiredMinutes > totalGapMinutes;

      refined.push({
        ...c,
        previousStopName,
        nextStopName,
        proximityMiles,
        distanceFromPrevMiles,
        distanceToNextMiles,
        driveFromPrevMinutes,
        driveToNextMinutes,
        tier,
        isEstimate,
        timeTight,
      });
    });
  }

  refined.sort((a, b) => {
    if (a.proximityMiles !== b.proximityMiles) return a.proximityMiles - b.proximityMiles;
    return a.meetingStartUtc.getTime() - b.meetingStartUtc.getTime();
  });

  // Omit near-zero mileage suggestions to avoid showing effectively duplicate slot items.
  const nonZero = refined.filter((r) => r.proximityMiles > 0.05 && !r.timeTight);

  const scheduled = nonZero.filter((r) => schedSet.has(String(r.status))).slice(0, 5);
  const cancelled = nonZero
    .filter((r) => cancelSet.has(String(r.status)) && r.proximityMiles <= CANCELLED_MAX_PROXIMITY_MILES)
    .slice(0, 5);

  return { scheduled, cancelled };
}
